import type { PlanCode } from "./apiContracts";
import type { PrototypeApiClient } from "./apiClient";
import type { Root } from "./prototypeTypes";
import {
  mountMediaPlayer,
  mountedFor,
	clearVideoEndedMediaSession,
  pauseMediaPlayer,
  playMediaPlayer,
  playVideoWithGestureRetry,
  prepareVideoMediaPlayerTransition,
  presentOptimisticAudioPlayback,
  sweepStrayMedia,
  unmountMediaPlayer,
} from "./mediaPlayerCore";
import { runtimeOf } from "./mediaPlayerProgress";
import { getTopic } from "./topicStore";
import { hidePlayer, previewNowPlayingChapter, showPlayer } from "./playerUiController";
import { showArticle } from "./navigationController";
import { showToast } from "./toastController";
import { paintContentTraceLabel } from './contentTraceLabel';
import {
  createMediaChapterTransitionCoordinator,
  isImmediateVideoTransitionRetry,
  type MediaChapterTransitionCoordinator,
} from './mediaChapterTransitionCoordinator';
import {
  presentMediaAccessDecision,
  presentPlaybackAccessError,
  resolveMediaAccessDecision,
} from './mediaPlayerAccessGate';
import { clearMediaNotReady, presentMediaNotReady, presentVideoNotReady } from './mediaPlayerReadiness';
import { scheduleHomeRecentReconcile } from './homeRecentHydrator';
import { beginAudioHandoffNavigationIntent, canCommitAudioHandoffExplicitPlay, type AudioExplicitPlayIntent } from './audioHandoffController';
import { closeVideoPresentationSurface, openVideoPresentationSurface } from './videoPresentationSurface';

export const chapterCountCache = new Map<string, number>();

// Generation guards prevent stale playback-token responses from mounting when
// the user rapidly switches chapters. Each navigation increments the counter;
// the async fetch checks whether its generation is still current before mounting.
// The AbortController ensures the underlying network request is actually
// cancelled (not just ignored) when a newer navigation supersedes it — this
// saves bandwidth and prevents late responses from arriving after the user
// has already moved on.
let audioChapterGeneration = 0;
let audioAbortController: AbortController | null = null;

interface ActiveVideoTransition {
  root: Root;
  topicId: string;
  plan: PlanCode;
  api: PrototypeApiClient;
  coordinator: MediaChapterTransitionCoordinator;
}

let activeVideoTransition: ActiveVideoTransition | null = null;

export async function getChapterCount(api: PrototypeApiClient, topicId: string): Promise<number> {
  if (chapterCountCache.has(topicId)) return chapterCountCache.get(topicId)!;
  try {
    const list = await api.listTopicChapters(topicId);
    const count = Array.isArray(list?.chapters) ? list.chapters.length : 0;
    if (count > 0) chapterCountCache.set(topicId, count);
    return count;
  } catch {
    return 0;
  }
}

export function navigateAndPlayAudio(
	root: Root,
	topicId: string,
	nextIndex: number,
	plan: PlanCode,
	api: PrototypeApiClient,
	intent?: AudioExplicitPlayIntent,
	options: { navigation?: 'follow-playback' | 'preserve-view'; resume?: 'saved' | 'start' } = {},
): void {
	const preserveView = options.navigation === 'preserve-view';
	if (options.resume === 'start') {
		const runtime = runtimeOf(root);
		const key = `${topicId}:${nextIndex}`;
		if (runtime) {
			runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: 0 };
			runtime.resumeProgressByChapter = { ...(runtime.resumeProgressByChapter ?? {}), [key]: 0 };
		}
	}
	const previousChapter = preserveView ? null : previousAudioChapter(root, topicId, nextIndex);
	if (!preserveView) {
		setRuntimeChapter(root, topicId, nextIndex);
		showArticle(nextIndex, root, plan);
	}
	// Every play intent funnels here (rail, article, panel prev/next, hero
	// continue): the dock, hero and rail must flip to the intended chapter
	// immediately instead of at token-return.
	const chapterMeta = getTopic(topicId);
	const nextTitle = [...(chapterMeta?.previewChapters ?? []), ...(chapterMeta?.lockedChapters ?? [])]
		.find((chapter) => chapter.index === nextIndex)?.title
		|| `Chapter ${nextIndex}`;
	previewNowPlayingChapter(root, {
		index: nextIndex,
		title: nextTitle,
		topicTitle: chapterMeta?.title || topicId,
		topicSlug: topicId,
		topicSubtitle: chapterMeta?.subtitle || '',
	});
  // Abort any in-flight token fetch from a previous chapter before starting a new one.
  audioAbortController?.abort();
  const controller = new AbortController();
  audioAbortController = controller;
  const generation = ++audioChapterGeneration;
  const navigationIntent = intent ?? beginAudioHandoffNavigationIntent(root, { topicId, chapterIndex: nextIndex });
	void fetchAndPlayAudio(root, api, topicId, nextIndex, plan, generation, navigationIntent, controller.signal, previousChapter, preserveView);
}

/** The chapter the runtime was on before this navigation, used to roll the
 * article/player back when the target chapter is denied by the paywall. */
function previousAudioChapter(root: Root, topicId: string, nextIndex: number): { topicId: string; chapterIndex: number } | null {
  const rt = runtimeOf(root);
  if (!rt?.activeCourseId || typeof rt.activeChapterIndex !== 'number' || rt.activeChapterIndex < 1) return null;
  if (rt.activeCourseId === topicId && rt.activeChapterIndex === nextIndex) return null;
  return { topicId: rt.activeCourseId, chapterIndex: rt.activeChapterIndex };
}

export function navigateToVideoChapter(root: Root, topicId: string, nextIndex: number, plan: PlanCode, api: PrototypeApiClient): void {
  const currentIndex = mountedFor(root, 'video')?.chapterIndex ?? runtimeOf(root)?.activeChapterIndex ?? nextIndex;
  videoTransitionFor(root, topicId, currentIndex, plan, api).goTo(nextIndex);
}

export function moveVideoChapter(root: Root, topicId: string, currentIndex: number, plan: PlanCode, api: PrototypeApiClient, direction: 1 | -1): void {
  videoTransitionFor(root, topicId, currentIndex, plan, api).move(direction);
}

export function retryVideoChapterTransition(): void {
  activeVideoTransition?.coordinator.retry();
}

export function stopVideoChapterTransitions(root?: Root): void {
  if (!activeVideoTransition || (root && activeVideoTransition.root !== root)) return;
  activeVideoTransition.coordinator.cleanup();
  activeVideoTransition = null;
}

async function fetchAndPlayAudio(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode, generation: number, intent: AudioExplicitPlayIntent, signal: AbortSignal, previous: { topicId: string; chapterIndex: number } | null = null, preserveView = false): Promise<void> {
	try {
		const response = await api.audioPlaybackToken({ topicId, chapterIndex, plan }, signal);
		// A newer chapter navigation may have started while we were awaiting - discard.
		if (generation !== audioChapterGeneration || !canCommitAudioHandoffExplicitPlay(root, intent)) {
			const leaseId = readPlaybackLeaseId(response);
			if (leaseId) void api.releasePlaybackLease(leaseId).catch(() => undefined);
			return;
		}
		if (mountMediaPlayer(root, response, { topicId, chapterIndex })) {
			clearMediaNotReady(root, 'audio');
			// The rail card must read the new chapter the moment its source is
			// mounted, not one write→reconcile round-trip later — advances and
			// dock next/prev have no click-time optimistic paint of their own.
			presentOptimisticAudioPlayback(root, {
				topicId,
				chapterIndex,
				chapterTitle: (root as unknown as { __nowPlayingChapter?: { title?: string } }).__nowPlayingChapter?.title,
				paused: true,
			});
			const playing = await playMediaPlayer(root, "audio");
			if (playing) {
				if (preserveView) {
					(root as Document | HTMLElement).dispatchEvent(new CustomEvent('deepmox:audio-handoff-ui', { detail: { topicId, chapterIndex } }));
				}
				// A panel-driven chapter switch is the user's newest Recent intent:
				// the rail's chapter/ring must follow without waiting for the next
				// progress write or a Home re-entry.
				scheduleHomeRecentReconcile(root, api);
			}
		}
	} catch (error) {
		if (signal.aborted || generation !== audioChapterGeneration || !canCommitAudioHandoffExplicitPlay(root, intent)) return;
    paintContentTraceLabel(root.querySelector<HTMLElement>('#webPlayer'), undefined, 'audio');
    pauseMediaPlayer(root, "audio");
    if (presentMediaNotReady(root, 'audio', error)) {
      unmountMediaPlayer(root, 'audio', { force: true });
      return;
    }
	if (presentPlaybackAccessError(root, 'audio', error, topicId)) {
      // The denied chapter must not keep a playable surface under its
      // identity: stop every audio element (including ones the registry
      // missed) and put the article/player back on the chapter the user came
      // from, mirroring the video paywall contract of returning to the
      // currently-playing chapter when the modal closes.
      pauseMediaPlayer(root, 'audio');
      sweepStrayMedia(root, 'audio');
      if (previous) {
        setRuntimeChapter(root, previous.topicId, previous.chapterIndex);
        showArticle(previous.chapterIndex, root, plan, { historyMode: 'replace' });
      }
      return;
    }
    showToast({ type: "error", message: "Audio playback failed. Please try again.", durationMs: 6000 });
    console.warn("[deepmox] audio playback failed", error);
  }
}

function setRuntimeChapter(root: Root, topicId: string, chapterIndex: number) {
  const rt = runtimeOf(root);
  if (!rt) return;
  rt.activeCourseId = topicId;
  rt.activeChapterIndex = chapterIndex;
}

function videoTransitionFor(root: Root, topicId: string, currentIndex: number, plan: PlanCode, api: PrototypeApiClient): MediaChapterTransitionCoordinator {
  if (
    activeVideoTransition
    && activeVideoTransition.root === root
    && activeVideoTransition.topicId === topicId
    && activeVideoTransition.plan === plan
    && activeVideoTransition.api === api
  ) {
    activeVideoTransition.coordinator.syncMounted(currentIndex);
    return activeVideoTransition.coordinator;
  }

  activeVideoTransition?.coordinator.cleanup();
  const mountedChapter = mountedFor(root, 'video')?.chapterIndex;
  const initialChapter = Number.isFinite(mountedChapter) && (mountedChapter ?? 0) > 0 ? mountedChapter! : currentIndex;
  let coordinator: MediaChapterTransitionCoordinator;
  coordinator = createMediaChapterTransitionCoordinator<unknown>({
    initialChapter,
    getTotalChapters: () => {
      const paintedCount = (root as unknown as { __videoChapterCount?: number }).__videoChapterCount;
      const cachedCount = chapterCountCache.get(topicId);
      if (paintedCount && paintedCount > 0) return paintedCount;
      if (cachedCount && cachedCount > 0) return cachedCount;
      return getChapterCount(api, topicId);
    },
    request: (chapterIndex) => api.videoPlaybackToken({ topicId, chapterIndex, plan }),
    readLeaseId: (response) => readPlaybackLeaseId(response),
    release: (playbackLeaseId) => api.releasePlaybackLease(playbackLeaseId),
    shouldBlockChapter: (chapterIndex) => {
      const blocked = checkVideoAccessBlock(root, topicId, chapterIndex);
      if (blocked) hideVideoLoadingOverlay(root);
      return blocked;
    },
    commit: async (chapterIndex, response, context) => {
      const result = await prepareVideoMediaPlayerTransition(root, response, {
        topicId,
        chapterIndex,
        signal: context.signal,
      });
      if (!result.committed) return result;
      clearMediaNotReady(root, 'video');
      setRuntimeChapter(root, topicId, chapterIndex);
      // Play as early as possible: iOS Safari's transient-activation window
      // from the swipe gesture expires during the async token fetch + video
      // load, so the play() attempt must not queue behind showArticle's heavy
      // DOM rebuild. If it still rejects, playVideoWithGestureRetry arms a
      // touch retry on the fullscreen surface.
      void playVideoWithGestureRetry(root);
      // Chapter switches inside the video player rewrite the current history
      // entry instead of pushing, so a browser Back closes the video instead
      // of walking back through every chapter the user swiped.
      showArticle(chapterIndex, root, plan, { historyMode: 'replace' });
      // The audio bar represents audio controls only. A video chapter switch
      // must not reveal it again while video (including PiP) owns the session.
      hidePlayer(root);
      return result;
    },
    onError: (message, failure) => {
      clearVideoEndedMediaSession(root);
      if (presentVideoNotReady(root, failure.chapter, failure.error, {
        retry: () => coordinator.retry(),
		continueReading: () => closeVideoPresentationSurface(root),
      })) {
		// A PiP surface remains mounted but hidden. Normalize it in the same
		// task as readiness presentation, before the browser can paint it.
		openVideoPresentationSurface(root);
        setRuntimeChapter(root, topicId, failure.chapter);
        showArticle(failure.chapter, root, plan);
        unmountMediaPlayer(root, 'video', { force: true });
        hideVideoLoadingOverlay(root);
        return;
      }
	  if (presentPlaybackAccessError(root, 'video', failure.error, topicId)) {
        hideVideoLoadingOverlay(root);
        return;
      }
      const immediateRetry = isImmediateVideoTransitionRetry(failure.error);
      showToast({
        type: 'error',
        message,
        durationMs: immediateRetry ? 0 : 6000,
        ...(immediateRetry ? { actionLabel: 'Retry', onAction: () => coordinator.retry() } : {}),
      });
      console.warn('[deepmox] video chapter transition failed', failure.error);
    },
    onTransitionStart: (chapter) => {
      clearMediaNotReady(root, 'video');
      showVideoLoadingOverlay(root);
      // Immediately move the breathing dot to the target chapter so the
      // indicator feels instant. The full paintVideoChrome (which fetches
      // chapter titles etc.) runs later in commit; without this the dot
      // only moves after the async token fetch + video load completes,
      // making the UI feel laggy.
      setRuntimeChapter(root, topicId, chapter);
      updateChapterDotsActive(root, chapter);
    },
    onTransitionEnd: () => hideVideoLoadingOverlay(root),
  });
  activeVideoTransition = { root, topicId, plan, api, coordinator };
  return coordinator;
}

function readPlaybackLeaseId(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') return undefined;
  const leaseId = (response as { playbackLeaseId?: unknown }).playbackLeaseId;
  return typeof leaseId === 'string' && leaseId.length > 0 ? leaseId : undefined;
}

function showVideoLoadingOverlay(root: Root): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const overlay = doc.getElementById('vfLoadingOverlay');
  overlay?.classList.add('active');
  overlay?.setAttribute('aria-hidden', 'false');
}

function hideVideoLoadingOverlay(root: Root): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const overlay = doc.getElementById('vfLoadingOverlay');
  overlay?.classList.remove('active');
  overlay?.setAttribute('aria-hidden', 'true');
}

/**
 * Immediately move the breathing-dot indicator to the target chapter without
 * waiting for the async token fetch + video load. Toggles `.active` / `.played`
 * classes on existing `.vf-dot` elements so the indicator feels instant.
 */
function updateChapterDotsActive(root: Root, activeIndex: number): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const dots = doc.getElementById('vfChapterDots');
  if (!dots) return;
  dots.querySelectorAll<HTMLElement>('.vf-dot').forEach((dot, i) => {
    const chapterIndex = i + 1; // dots are 1-indexed (chapter 1, 2, ...)
    dot.classList.toggle('active', chapterIndex === activeIndex);
    dot.classList.toggle('played', chapterIndex < activeIndex);
  });
}

/**
 * Client-side L1 access pre-check for video chapter transitions (swipe / next /
 * prev inside the video fullscreen). Without this, sliding from a free chapter
 * to a paid one sends a token request that the server rejects with 403
 * `entitlement_required`, which the coordinator mislabels as "Video couldn't
 * load" instead of presenting the paywall.
 *
 * Uses the shared presentAccessDecision entry point so the paywall modal
 * (not a toast) is shown -- identical to the CTA click path. The modal
 * overlays the video fullscreen; closing it returns the user to the
 * currently-playing chapter's video controls.
 */
function checkVideoAccessBlock(root: Root, topicId: string, chapterIndex: number): boolean {
  const decision = resolveMediaAccessDecision(root, 'video', topicId, chapterIndex);
  if (decision.kind === 'allow') return false;
  return presentMediaAccessDecision(root, decision, 'swipe');
}
